import * as React from 'react';

import * as Sentry from '@sentry/react';

import { useLocation } from 'js/lib/useRouter';

import { AVAILABLE_SPACES } from 'bundles/cms/utils/SpaceUtils';
import { NotFound } from 'bundles/common/components/withNotFound';
import { useLocalizedContent } from 'bundles/internationalization-lib/hooks/useLocalizedContent';
import type { CareerAcademyPageContentFieldsFragment } from 'bundles/unified-career-academy/__generated__/contentfulTypes';
import ConsumerPathwayCareerAcademyPageDataContext from 'bundles/unified-career-academy/contexts/ConsumerPathwayCareerAcademyPageDataContext';
import { CareerAcademyPageContentFragmentDoc } from 'bundles/unified-career-academy/queries/contentfulCareerAcademyPageContentQuery';

type Props = {
  children: React.ReactNode;
};

const PAGE_SLUG = 'consumer';

const ConsumerPathwayCareerAcademyDataProvider = ({ children }: Props) => {
  const { query } = useLocation();
  const isPreview = query.preview === 'true';

  const {
    data: pageContent,
    loading: pageContentLoading,
    error: pageContentError,
  } = useLocalizedContent<CareerAcademyPageContentFieldsFragment>({
    isPreview,
    contentfulSpace: AVAILABLE_SPACES.marketing.toLowerCase(),
    contentType: 'careerAcademyPageContent',
    fragmentDoc: CareerAcademyPageContentFragmentDoc,
    fragmentFieldsName: 'CareerAcademyPageContentFields',
    filters: {
      slug: PAGE_SLUG,
    },
    ssr: true,
    onError: (error) =>
      Sentry.captureException(error, {
        extra: {
          message: 'Error fetching localized content for Consumer Career Academy Page',
        },
      }),
  });

  if (pageContentError) {
    return <NotFound />;
  }

  return (
    <ConsumerPathwayCareerAcademyPageDataContext.Provider
      value={{
        contentfulContent: pageContent,
        loading: pageContentLoading,
      }}
    >
      {children}
    </ConsumerPathwayCareerAcademyPageDataContext.Provider>
  );
};

export default ConsumerPathwayCareerAcademyDataProvider;
